/**
 * The declaration vocabulary — what a resident or a caretaker can say about a
 * problem without being asked to think like a syndic.
 *
 * Deliberately GENERIC for now. The next step is a per-equipment refinement
 * (a lift jams, a boiler loses pressure, a door lock seizes), which is why the
 * codes live in one list a specialisation can override rather than being
 * scattered through the form. Everything here is optional: a declaration with
 * nothing but an asset and a timestamp is still actionable, and a form that
 * demands taxonomy from someone standing in a plant room gets abandoned.
 *
 * Codes are English and machine-readable (repo rule); the French the declarer
 * reads comes from i18n. They are stamped into the issue's `properties`, so
 * renaming one is a data migration — add rather than rename.
 */

/** What is wrong. Stamped as `properties.problem_type`. */
export const PROBLEM_TYPES = [
    'not_working',
    'intermittent',
    'damaged',
    'leak',
    'noise',
    'smell',
    'blocked',
    'dirty',
    'unsafe',
    'other',
] as const

export type ProblemType = (typeof PROBLEM_TYPES)[number]

/**
 * Why it is wrong, as far as the declarer can tell. Stamped as
 * `properties.cause`.
 *
 * This is the field that decides whether the ticket is an insurance matter or
 * a maintenance one — vandalism, water, fire, storm and theft are claims; wear
 * is a contract's job. The declarer is never asked to make that call, they are
 * asked what they saw; the syndic reads the answer.
 */
export const PROBLEM_CAUSES = [
    'vandalism',
    'water',
    'fire',
    'storm',
    'frost',
    'accident',
    'theft',
    'wear',
    'unknown',
] as const

export type ProblemCause = (typeof PROBLEM_CAUSES)[number]

/** Payload the declaration writes into `issue.properties`. */
export type DeclarationProperties = {
    /** ISO instant the problem was observed. Defaults to the moment of filing
     * — a declaration is always about something, and "now" is a far better
     * guess than nothing when the declarer skipped the field. */
    occurred_at: string
    /** Whether `occurred_at` was stated or assumed, so a reader never mistakes
     * the filing time for an observation. */
    occurred_at_source: 'declared' | 'filing_time'
    problem_type?: ProblemType
    cause?: ProblemCause
    /** Document uid of the recorded voice note, when one was left. */
    voice_note_document_uid?: string
    /**
     * What the model heard, before the declarer touched it.
     *
     * The corrected text goes to `description` — that is what the syndic acts
     * on. This keeps the machine's own version beside it, which is the only way
     * to tell later whether a wrong ticket came from a mis-hearing or from a
     * mis-statement. Absent when nothing was recorded, or when the recording
     * held no speech.
     */
    voice_transcript?: string
    /**
     * Document uids of the photographs taken with the declaration, oldest
     * first.
     *
     * They document the EVENT, not the equipment: the ticket around them
     * carries the date, the author and what was declared, which a photo filed
     * as the asset's reference picture would never carry — and which is why
     * they do not touch that picture. Typed `INCIDENT_PICTURE` so they never
     * get mistaken for one either.
     */
    photo_document_uids?: string[]
    /** Where the declaration was made from, for later analysis of the QR loop. */
    declared_from: 'photo_tab' | 'issues_tab'
}
